import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, RotateCw } from "lucide-react";

// Full-bleed wrapper for the pre-app + post-app story slides
// (cover, setup, amendment, punchline). The middle slides (4-7) render
// the real ICF app screens with StoryNarratorBar overlaid.

// Clears any story-run state so the visitor can watch the extraction
// magic-moment again. Used by every Restart affordance on every slide.
export const restartStory = (navigate) => {
    try {
        window.sessionStorage.removeItem("icf_extracted");
    } catch (_e) {
        /* private mode */
    }
    navigate("/icf/story");
};

export default function StoryLayout({
    slideNumber,
    totalSlides = 8,
    eyebrow,
    children,
    nextTo,
    nextLabel = "Next",
    prevTo,
    variant = "light", // "light" | "dark"
    testid,
}) {
    const navigate = useNavigate();
    const bg = variant === "dark" ? "bg-[#0A0A0A]" : "bg-[#FAFAFA]";
    const fg = variant === "dark" ? "text-white" : "text-[#0A0A0A]";
    const subFg = variant === "dark" ? "text-neutral-400" : "text-neutral-500";
    const borderHair =
        variant === "dark" ? "border-[#262626]" : "border-hair";

    return (
        <div
            className={`min-h-screen ${bg} ${fg} flex flex-col`}
            data-testid={testid}
        >
            {/* header */}
            <header className={`border-b ${borderHair}`}>
                <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center gap-4 flex-wrap">
                    <Link to="/" className="flex items-center gap-2">
                        <img
                            src="/audri-logo.png"
                            alt="AudRI"
                            className="w-6 h-6 rounded-sm"
                        />
                        <span className="heading font-bold text-[15px] leading-none">
                            audri
                        </span>
                        <span
                            className={`overline text-[9px] ml-1 ${subFg}`}
                        >
                            for ICF · Clinical Trials · Story
                        </span>
                    </Link>
                    <div
                        className={`ml-auto mono text-[10px] uppercase tracking-wider ${subFg}`}
                    >
                        {slideNumber} / {totalSlides}
                    </div>
                    <button
                        type="button"
                        onClick={() => restartStory(navigate)}
                        data-testid="story-restart"
                        className={`text-[11px] ${subFg} hover:underline inline-flex items-center gap-1`}
                    >
                        <RotateCw size={11} /> Restart story
                    </button>
                    <Link
                        to="/icf/extract"
                        data-testid="story-skip"
                        className={`text-[11px] ${subFg} hover:underline`}
                    >
                        Skip intro · Explore the app →
                    </Link>
                </div>
            </header>

            {/* progress ticker */}
            <div className={`h-[2px] ${variant === "dark" ? "bg-[#171717]" : "bg-neutral-100"}`}>
                <div
                    className="h-full bg-[#C2410C] transition-all duration-500"
                    style={{ width: `${(slideNumber / totalSlides) * 100}%` }}
                />
            </div>

            {/* body */}
            <main className="flex-1 flex flex-col justify-center max-w-6xl w-full mx-auto px-6 md:px-10 py-12 md:py-16">
                {eyebrow && (
                    <div
                        className={`overline mb-6 ${
                            variant === "dark" ? "text-neutral-500" : ""
                        }`}
                    >
                        {eyebrow}
                    </div>
                )}
                {children}
            </main>

            {/* footer nav */}
            <footer className={`border-t ${borderHair}`}>
                <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center gap-3 flex-wrap">
                    {prevTo ? (
                        <button
                            type="button"
                            onClick={() => navigate(prevTo)}
                            data-testid="story-prev"
                            className={`inline-flex items-center gap-2 text-[12px] ${subFg} hover:${fg} transition-colors`}
                        >
                            <ArrowLeft size={12} /> Back
                        </button>
                    ) : (
                        <div />
                    )}
                    <div className="ml-auto flex items-center gap-3">
                        {nextTo && (
                            <button
                                type="button"
                                onClick={() => navigate(nextTo)}
                                data-testid="story-next"
                                className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-sm px-4 py-2.5 rounded-sm hover:bg-[#9A3412] transition-colors"
                            >
                                {nextLabel}
                                <ArrowRight size={14} />
                            </button>
                        )}
                    </div>
                </div>
            </footer>
        </div>
    );
}
